import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Empty, Pagination, Select, Space, Tag, Typography, message } from 'antd';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftOutlined, DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import dermatoApi, { TimelineEventType, type TimelineEvent } from '../../api/dermatoApi';
import { dermatoDocumentsApi } from '../../api/dermatoDocumentsApi';
import DocumentPreviewModal from '../../components/common/DocumentPreviewModal';

const { Title, Text } = Typography;

/** Etichete RO pentru tipurile de eveniment. */
export const TIMELINE_TYPE_LABELS: Record<string, string> = {
  FLOW_EVENT: 'Eveniment de flux',
  DOCUMENT_UPLOADED: 'Document încărcat',
  DOCUMENT_GENERATED: 'Document generat',
  COMMISSION_DECISION: 'Decizie comisie',
  TASK: 'Sarcină',
};

/** Culorile tag-urilor (AntD) per tip de eveniment. */
export const TIMELINE_TYPE_COLORS: Record<string, string> = {
  FLOW_EVENT: 'blue',
  DOCUMENT_UPLOADED: 'green',
  DOCUMENT_GENERATED: 'purple',
  COMMISSION_DECISION: 'orange',
  TASK: 'gold',
};

const MONTHS_RO = [
  'ianuarie',
  'februarie',
  'martie',
  'aprilie',
  'mai',
  'iunie',
  'iulie',
  'august',
  'septembrie',
  'octombrie',
  'noiembrie',
  'decembrie',
];

export const ALL_TYPES = 'ALL';

const typeLabel = (type: string): string => TIMELINE_TYPE_LABELS[type] ?? type;

const monthLabel = (date: Date): string =>
  `${MONTHS_RO[date.getMonth()]} ${date.getFullYear()}`;

const dayLabel = (date: Date): string =>
  `${date.getDate()} ${MONTHS_RO[date.getMonth()]} ${date.getFullYear()}`;

interface DayGroup {
  key: string;
  label: string;
  items: TimelineEvent[];
}

interface MonthGroup {
  key: string;
  label: string;
  days: DayGroup[];
}

/** Grupează evenimentele (descrescător) pe lună și apoi pe zi. */
export const groupTimeline = (events: TimelineEvent[]): MonthGroup[] => {
  const months: MonthGroup[] = [];

  for (const event of events) {
    const occurred = new Date(event.occurredAt);
    if (Number.isNaN(occurred.getTime())) continue;

    const monthKey = `${occurred.getFullYear()}-${occurred.getMonth()}`;
    let month = months.find((m) => m.key === monthKey);
    if (!month) {
      month = { key: monthKey, label: monthLabel(occurred), days: [] };
      months.push(month);
    }

    const dayKey = `${monthKey}-${occurred.getDate()}`;
    let day = month.days.find((d) => d.key === dayKey);
    if (!day) {
      day = { key: dayKey, label: dayLabel(occurred), items: [] };
      month.days.push(day);
    }

    day.items.push(event);
  }

  return months;
};

/**
 * Cronologia formularului DERMATO (§2.10.1–§2.10.3).
 *
 * Lista cronologică descrescătoare, grupată pe lună și zi, cu filtru după
 * tipul evenimentului și paginare.
 */
const DermatoTimelinePage = () => {
  const { formId } = useParams<{ formId: string }>();
  const navigate = useNavigate();
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [type, setType] = useState<string>(ALL_TYPES);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!formId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await dermatoApi.getTimeline(formId, {
        type: type === ALL_TYPES ? undefined : (type as TimelineEventType),
        page,
        pageSize,
      });
      setEvents(res?.items ?? []);
      setTotal(res?.total ?? 0);
    } catch {
      setEvents([]);
      setTotal(0);
      setError('Eroare la încărcarea cronologiei formularului.');
    } finally {
      setLoading(false);
    }
  }, [formId, type, page, pageSize]);

  useEffect(() => {
    void load();
  }, [load]);

  const groups = useMemo(() => groupTimeline(events), [events]);

  /**
   * Descărcarea documentului direct din cronologie (registru).
   * Aceleași flux ca în pasul „Raport Fotofinder" din wizard.
   */
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const handleDownload = async (event: TimelineEvent) => {
    if (!formId || !event.documentId) return;
    try {
      setDownloadingId(event.documentId);
      await dermatoDocumentsApi.downloadDocument(formId, event.documentId, event.fileName ?? undefined);
    } catch {
      message.error('Eroare la descărcarea documentului.');
    } finally {
      setDownloadingId(null);
    }
  };

  /** Documentul deschis în previzualizare (null = fereastra închisă). */
  const [preview, setPreview] = useState<{ documentId: string; fileName?: string | null } | null>(null);

  const typeOptions = useMemo(
    () => [
      { value: ALL_TYPES, label: 'Toate' },
      ...Object.values(TimelineEventType).map((value) => ({
        value,
        label: typeLabel(value),
      })),
    ],
    [],
  );

  return (
    <Card>
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <Button
          size="small"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
          data-testid="timeline-back"
        >
          Înapoi la registru
        </Button>
        <Title level={4} style={{ marginTop: 0, marginBottom: 0 }}>
          Cronologie formular
        </Title>
        <Text type="secondary">
          Evenimentele formularului, de la cel mai recent la cel mai vechi. Documentele încărcate (ex.
          raportul Fotofinder) se descarcă de aici.
        </Text>
        <Space style={{ marginTop: 12 }} data-testid="timeline-filter">
          <Text>Tip eveniment</Text>
          <Select
            value={type}
            onChange={(value: string) => {
              setType(value);
              setPage(1);
            }}
            options={typeOptions}
            style={{ width: 220 }}
            data-testid="timeline-type-select"
          />
        </Space>
      </Space>

      {error && (
        <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} data-testid="timeline-error" />
      )}

      {loading && <Alert type="info" message="Se încarcă cronologia…" />}

      {!loading && groups.length === 0 && !error && (
        <Empty description="Nu există evenimente pentru acest formular." />
      )}

      {groups.map((month) => (
        <div key={month.key} style={{ marginTop: 16 }} data-testid="timeline-month">
          <Title level={5} style={{ marginBottom: 8 }}>
            {month.label}
          </Title>
          {month.days.map((day) => (
            <div
              key={day.key}
              style={{ marginLeft: 12, marginBottom: 12 }}
              data-testid="timeline-day"
            >
              <Text strong>{day.label}</Text>
              {day.items.map((event) => (
                <div
                  key={event.id}
                  style={{
                    borderLeft: '2px solid #e8e8e8',
                    paddingLeft: 12,
                    marginTop: 8,
                  }}
                  data-testid="timeline-event"
                >
                  <Space direction="vertical" size={2} style={{ width: '100%' }}>
                    <Space>
                      <Tag color={TIMELINE_TYPE_COLORS[event.type] ?? 'default'}>
                        {typeLabel(event.type)}
                      </Tag>
                      <Text strong>{event.title}</Text>
                      <Text type="secondary">
                        {new Date(event.occurredAt).toLocaleString('ro-RO')}
                      </Text>
                    </Space>
                    {event.description && <Text>{event.description}</Text>}
                    {event.actor && <Text type="secondary">Executant: {event.actor}</Text>}
                    <Space size={8} wrap>
                      {event.documentId && (
                        <Button
                          size="small"
                          icon={<EyeOutlined />}
                          onClick={() =>
                            setPreview({ documentId: event.documentId as string, fileName: event.fileName ?? null })
                          }
                          data-testid="timeline-view"
                        >
                          Vezi
                        </Button>
                      )}
                      {event.documentId && (
                        <Button
                          size="small"
                          icon={<DownloadOutlined />}
                          loading={downloadingId === event.documentId}
                          onClick={() => void handleDownload(event)}
                          data-testid="timeline-download"
                        >
                          Descarcă
                        </Button>
                      )}
                      {event.link && (
                        <Link to={event.link} data-testid="timeline-link">
                          Vezi entitatea de origine
                        </Link>
                      )}
                    </Space>
                  </Space>
                </div>
              ))}
            </div>
          ))}
        </div>
      ))}

      {total > 0 && (
        <Pagination
          current={page}
          pageSize={pageSize}
          total={total}
          onChange={(next: number) => setPage(next)}
          style={{ marginTop: 16 }}
          data-testid="timeline-pagination"
        />
      )}

      <DocumentPreviewModal
        open={preview !== null}
        formId={formId ?? ''}
        documentId={preview?.documentId ?? null}
        fileName={preview?.fileName ?? null}
        onClose={() => setPreview(null)}
      />
    </Card>
  );
};

export default DermatoTimelinePage;
